import React, { useState, useEffect } from "react";
import Header from "./components/Header";
import AvatarStrip from "./components/AvatarStrip";
import RemindersSection from "./components/RemindersSection";
import TasksSection from "./components/TasksSection";
import CalendarSection from "./components/CalendarSection";
import AllTasksPage from "./components/AllTasksPage";
import KidDashboard from "./components/KidDashboard";
import TodayTomorrowSection from "./components/TodayTomorrowSection";
import CarissaDashboard from "./components/CarissaDashboard";
import RobDashboard from "./components/RobDashboard";
import SchoolActivitiesPage from "./components/SchoolActivitiesPage";
import SchoolDetail from "./components/SchoolDetail";
import ActivityDetail from "./components/ActivityDetail";
import EssentialInfoPage, { NoteDetail, NotebookView } from "./components/EssentialInfoPage";
import QuickLinksPage from "./components/QuickLinksPage";
import DriveLinksPage from "./components/DriveLinksPage";
import TripsPage, { PlacesPage, HotelsPage } from "./components/TripsPage";
import BucketListPage from "./components/BucketListPage";
import GiftsPage from "./components/GiftsPage";
import KidsGiftsPage from "./components/KidsGiftsPage";
import FamilyProjectsPage from "./components/FamilyProjectsPage";
import ShoppingPage from "./components/ShoppingPage";
import AddTaskSheet from "./components/AddTaskSheet";
import BottomNav from "./components/BottomNav";
import "./App.css";

const KID_PERSONS = ["Kimberly", "Adam"];
const NAV_STORAGE_KEY = "potterton-nav-stack";

// Maps each kid's Google account to their person name, so they land on
// their own dashboard instead of the shared family Home page. Update these
// if the actual account emails differ.
const EMAIL_TO_PERSON = {
  "adam.c.potterton@gmail.com": "Adam",
  "kimberly.potterton@gmail.com": "Kimberly",
};

function readNavStack(currentUser) {
  try {
    const raw = sessionStorage.getItem(NAV_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  const kidPerson = currentUser?.email && EMAIL_TO_PERSON[currentUser.email.toLowerCase()];
  if (kidPerson) return [{ page: "kid-dashboard", param: kidPerson }];
  return [{ page: "home", param: null }];
}

function App({ currentUser, onSignOut }) {
  const isKidUser = !!currentUser?.isPin || !!(currentUser?.email && EMAIL_TO_PERSON[currentUser.email.toLowerCase()]);
  const [navStack, setNavStack] = useState(() => readNavStack(currentUser));
  const [defaultView, setDefaultView] = useState("all");
  const [reminders, setReminders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(true);
  const [loadingTasks, setLoadingTasks] = useState(true);
  const [error, setError] = useState(null);
  const [showAddTask, setShowAddTask] = useState(false);

  const current = navStack[navStack.length - 1];
  const { page, param: pageParam } = current;

  useEffect(() => {
    try { sessionStorage.setItem(NAV_STORAGE_KEY, JSON.stringify(navStack)); } catch {}
  }, [navStack]);

  useEffect(() => { fetchReminders(); fetchTasks(); }, []);

  const fetchReminders = async () => {
    try { const res = await fetch("/.netlify/functions/notion?type=reminders"); const data = await res.json(); setReminders(data.items || []); }
    catch { setError("Could not load reminders"); } finally { setLoadingReminders(false); }
  };
  const fetchTasks = async () => {
    try { const res = await fetch("/.netlify/functions/notion?type=tasks"); const data = await res.json(); setTasks(data.items || []); }
    catch { setError("Could not load tasks"); } finally { setLoadingTasks(false); }
  };

  const push = (p, param = null) => setNavStack(s => [...s, { page: p, param }]);
  const pop = () => setNavStack(s => s.length > 1 ? s.slice(0, -1) : s);
  const goHome = () => setNavStack([{ page: "home", param: null }]);

  const handleAvatarTap = (person) => {
    if (person === "Carissa") push("carissa-dashboard");
    else if (person === "Robert") push("rob-dashboard");
    else if (KID_PERSONS.includes(person)) push("kid-dashboard", person);
  };

  const handleTabTap = (tab) => {
    if (tab === "home") goHome();
    if (tab === "school") { goHome(); push("school-activities"); }
    if (tab === "links") { goHome(); push("quick-links"); }
    if (tab === "essential") { goHome(); push("essential-info"); }
  };

  const handlePageTap = (p) => {
    if (p === "shopping") push("shopping");
    if (p === "trips") push("trips");
    if (p === "bucket-list") push("bucket-list");
    if (p === "gifts") push("gifts");
    if (p === "family-projects") push("family-projects");
    if (p === "drive-links") push("drive-links");
  };

  const handleTaskAdded = (item) => { setTasks(prev => [item, ...prev]); };

  // Shared note/notebook nav — used by both EssentialInfoPage and CarissaDashboard
  const handleNotebookTap = (notebook, categories = null) => {
    push("notebook-view", { notebook, categories, activeTab: page });
  };
  const handleNoteTap = (note, activeTab = null) => {
    push("note-detail", { note, activeTab: activeTab || page });
  };

  if (page === "all-tasks") return <AllTasksPage onBack={pop} defaultView={defaultView} onTabTap={handleTabTap} />;

  if (page === "carissa-dashboard") return (
    <CarissaDashboard onBack={pop} onActivityTap={(act) => push("activity-detail", act)} onTabTap={handleTabTap}
      onSeeAllReminders={() => { setDefaultView("reminders"); push("all-tasks"); }}
      onSeeAllTasks={() => { setDefaultView("tasks"); push("all-tasks"); }}
      onSeeAllActivities={() => { goHome(); push("school-activities"); }}
      onNotebookTap={handleNotebookTap}
      onNoteTap={handleNoteTap} />
  );

  if (page === "rob-dashboard") return (
    <RobDashboard onBack={pop} onActivityTap={(act) => push("activity-detail", act)} onTabTap={handleTabTap}
      onSeeAllReminders={() => { setDefaultView("reminders"); push("all-tasks"); }}
      onSeeAllTasks={() => { setDefaultView("tasks"); push("all-tasks"); }}
      onSeeAllActivities={() => { goHome(); push("school-activities"); }} />
  );

  if (page === "note-detail") return (
    <NoteDetail note={pageParam.note} onBack={pop} onTabTap={handleTabTap} activeTab={pageParam.activeTab || "essential"} />
  );

  if (page === "notebook-view") return (
    <NotebookView
      notebook={pageParam.notebook}
      categories={pageParam.categories || null}
      onBack={pop}
      onNoteTap={(note) => push("note-detail", { note, activeTab: pageParam.activeTab || "essential" })}
      onTabTap={handleTabTap}
      activeTab={pageParam.activeTab || "essential"}
    />
  );

  if (page === "essential-info") return (
    <EssentialInfoPage
      onTabTap={handleTabTap}
      onNotebookTap={handleNotebookTap}
      onNoteTap={handleNoteTap}
    />
  );

  if (page === "kid-dashboard") return <KidDashboard person={pageParam} onBack={pop} onSchoolTap={(school) => push("kid-school-detail", school)} onActivityTap={(act) => push("kid-activity-detail", act)} onTabTap={handleTabTap} />;
  if (page === "school-activities") return <SchoolActivitiesPage onBack={pop} onTabTap={handleTabTap} />;
  if (page === "school-detail") return <SchoolDetail school={pageParam} onBack={pop} onTabTap={handleTabTap} />;
  if (page === "kid-school-detail") return <SchoolDetail school={pageParam} onBack={pop} onTabTap={handleTabTap} readOnly />;
  if (page === "activity-detail") return <ActivityDetail activity={pageParam} onBack={pop} onTabTap={handleTabTap} />;
  if (page === "kid-activity-detail") return <ActivityDetail activity={pageParam} onBack={pop} onTabTap={handleTabTap} readOnly />;
  if (page === "quick-links") return <QuickLinksPage onTabTap={handleTabTap} onPageTap={handlePageTap} />;
  if (page === "drive-links") return <DriveLinksPage onBack={pop} onTabTap={handleTabTap} readOnly={isKidUser} />;
  if (page === "trips") return <TripsPage onBack={pop} onTabTap={handleTabTap} onPlacesTap={(id) => push("places", id ? { highlightId: id } : null)} onHotelsTap={(id) => push("hotels", id ? { highlightId: id } : null)} readOnly={isKidUser} />;
  if (page === "places") return <PlacesPage onBack={pop} onTabTap={handleTabTap} highlightId={pageParam?.highlightId} readOnly={isKidUser} />;
  if (page === "hotels") return <HotelsPage onBack={pop} onTabTap={handleTabTap} highlightId={pageParam?.highlightId} readOnly={isKidUser} />;
  if (page === "bucket-list") return <BucketListPage onBack={pop} onTabTap={handleTabTap} readOnly={isKidUser} />;
  if (page === "gifts") return <GiftsPage onBack={pop} onTabTap={handleTabTap} onKidsGiftsTap={() => push("kids-gifts")} readOnly={isKidUser} />;
  if (page === "kids-gifts") return <KidsGiftsPage onBack={pop} onTabTap={handleTabTap} readOnly={isKidUser} />;
  if (page === "family-projects") return <FamilyProjectsPage onBack={pop} onTabTap={handleTabTap} readOnly={isKidUser} />;
  if (page === "shopping") return <ShoppingPage onBack={pop} onTabTap={handleTabTap} isKidUser={isKidUser} />;

  return (
    <>
      <div className="app">
        <Header />
        <AvatarStrip onAvatarTap={handleAvatarTap} />
        <div className="scroll-body">
          <TodayTomorrowSection onKidTap={(person) => push("kid-dashboard", person)} />
          <div className="section-divider" />
          <RemindersSection items={reminders} loading={loadingReminders} onSeeAll={() => { setDefaultView("reminders"); push("all-tasks"); }} />
          <div className="section-divider" />
          <TasksSection items={tasks} loading={loadingTasks} onSeeAll={() => { setDefaultView("tasks"); push("all-tasks"); }} onAdd={() => setShowAddTask(true)} />
          <div className="section-divider" />
          <CalendarSection view="main" />
          {currentUser && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "18px 20px 6px", fontSize: 12, color: "var(--muted)" }}>
              <span>Signed in as {currentUser.name || currentUser.email}</span>
              <button onClick={onSignOut} style={{ background: "none", border: "none", color: "var(--sage)", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>Sign out</button>
            </div>
          )}
        </div>
        <BottomNav active="home" onTabTap={handleTabTap} />
        {error && <div className="error-banner">{error}</div>}
        <div onClick={() => setShowAddTask(true)} style={{ position: "fixed", bottom: 88, right: "calc(50% - 195px + 16px)", width: 52, height: 52, borderRadius: "50%", background: "var(--sage)", boxShadow: "0 4px 16px rgba(122,158,135,0.4)", cursor: "pointer", zIndex: 99 }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 22, height: 3, background: "white", borderRadius: 2 }} />
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 3, height: 22, background: "white", borderRadius: 2 }} />
        </div>
      </div>
      {showAddTask && <AddTaskSheet onClose={() => setShowAddTask(false)} onAdd={handleTaskAdded} />}
    </>
  );
}

export default App;
